import { Checkbox } from 'antd';
import { useEffect, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { signalingColumns } from '@src/constants/columns';
import { Bars2Icon } from '@heroicons/react/24/outline';

interface ColumnSelectorProps {
    selectedKeys: string[];
    setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

interface SortableItemProps {
    id: string;
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}

export const ColumnSelector = ({
    selectedKeys,
    setSelectedKeys,
}: ColumnSelectorProps) => {
    const [columns, setColumns] = useState(signalingColumns);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const savedColumns = localStorage.getItem('signalingEventsColumns');
        const savedSelected = localStorage.getItem(
            'signalingEventsSelectedKeys'
        );

        let loadedColumns = signalingColumns;
        if (savedColumns) {
            const parsed = JSON.parse(savedColumns);
            const missing = signalingColumns.filter(
                (col) => !parsed.find((p) => p.key === col.key)
            );
            loadedColumns = [...parsed, ...missing];
        }

        setColumns(loadedColumns);

        if (savedSelected) {
            setSelectedKeys(JSON.parse(savedSelected));
        } else {
            setSelectedKeys(signalingColumns.map((col) => col.key));
        }

        setLoaded(true);
    }, []);

    const handleCheckboxChange = (key: string, checked: boolean) => {
        const newKeys = checked
            ? [...selectedKeys, key]
            : selectedKeys.filter((k) => k !== key);

        setSelectedKeys(newKeys);
        localStorage.setItem(
            'signalingEventsSelectedKeys',
            JSON.stringify(newKeys)
        );
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = columns.findIndex((i) => i.key === active.id);
            const newIndex = columns.findIndex((i) => i.key === over?.id);
            const newOrder = arrayMove(columns, oldIndex, newIndex);
            setColumns(newOrder);
            localStorage.setItem(
                'signalingEventsColumns',
                JSON.stringify(newOrder)
            );
        }
    };

    if (!loaded) return null;

    return (
        <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={columns.map((col) => col.key)}
                strategy={verticalListSortingStrategy}
            >
                {columns.map((col) => (
                    <SortableItem
                        key={col.key}
                        id={col.key}
                        label={col.label}
                        checked={selectedKeys.includes(col.key)}
                        onChange={(checked) =>
                            handleCheckboxChange(col.key, checked)
                        }
                    />
                ))}
            </SortableContext>
        </DndContext>
    );
};

const SortableItem = ({ id, label, checked, onChange }: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            className={`flex items-center justify-between px-6 py-3 bg-infoContainerLight border border-primary rounded-xl mb-2`}
        >
            <Checkbox
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="text-primary"
            >
                {label}
            </Checkbox>
            <Bars2Icon
                className="cursor-move"
                width={16}
                height={16}
                {...listeners}
            />
        </div>
    );
};
